import { ID_TOKEN_KEY } from '../constants';
import {
  ApplicationError,
  BusinessCheckError,
  ForbiddenError,
  NetworkError,
  ObjectNotFoundError,
  OptimisticLockError,
  SystemError,
  UnauthorizedError,
} from './ApplicationError';

function href(path: string) {
  return `${process.env.ORIGIN_SERVER_URL}${path}`;
}

function initRequest(): RequestInit {
  return {
    mode: 'cors',
    credentials: 'include',
    headers: new Headers({
      'Content-Type': 'application/json',
      Authorization: localStorage.getItem(ID_TOKEN_KEY) ?? '',
    }),
  };
}

async function handleInvalidRes(res: Response) {
  const payload = (await res.json()) as unknown;

  switch (res.status) {
    case 400:
      return new BusinessCheckError(payload);
    case 401:
      return new UnauthorizedError(payload);
    case 403:
      return new ForbiddenError(payload);
    case 404:
      return new ObjectNotFoundError(payload);
    case 409:
      return new OptimisticLockError(payload);

    default:
      return new SystemError(payload);
  }
}

async function handleApiLogToPendo(path: string, init: RequestInit, res: Response) {
  if (init.method && init.method.toUpperCase() === 'GET') return;
  const filterUrls = ['admin/tenant'];

  if (res.url) {
    const filterUrl = filterUrls.find((element) => res.url.includes(element));
    if (init.method && init.method.toUpperCase() === 'POST' && filterUrl) {
      if (window.pendo) {
        const payload = {
          event: 'submitForm',
          timestamp: Date.now(),
          url: res.url,
          form: init.body,
          responseStatus: res.status,
          responseOk: res.ok,
          responseJson: await res.json(),
        };
        window.pendo.track('onboarding_front', payload);
      }
    }
  }
}

async function safeFetch<T>(path: string, init: RequestInit) {
  try {
    const res = await fetch(path, init);
    await handleApiLogToPendo(path, init, res);
    if (res.ok) {
      return (await res.json()) as T;
    } else {
      throw await handleInvalidRes(res);
    }
  } catch (e) {
    if (!(e instanceof ApplicationError)) throw new NetworkError();
    throw e;
  }
}

function serializeToSearchParams(params: Record<string, number | string>) {
  return Object.keys(params).length === 0
    ? ''
    : `?${new URLSearchParams(
        Object.fromEntries(Object.entries(params).map(([k, v]) => [k, v.toString()])),
      ).toString()}`;
}

async function get<T>(path: string, params: Record<string, number | string> = {}) {
  return safeFetch<T>(`${href(path)}${serializeToSearchParams(params)}`, {
    ...initRequest(),
    method: 'GET',
  });
}

async function post<T>(path: string, body: object = {}) {
  return safeFetch<T>(href(path), {
    ...initRequest(),
    method: 'POST',
    body: JSON.stringify(body),
  });
}

async function put<T>(path: string, body: object = {}) {
  return safeFetch<T>(href(path), {
    ...initRequest(),
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

async function del<T>(path: string, body: object = {}) {
  return safeFetch<T>(href(path), {
    ...initRequest(),
    method: 'DELETE',
    body: JSON.stringify(body),
  });
}

export { del, get, post, put, serializeToSearchParams };
